import { createEffect, createResource, createSignal, For, onCleanup, onMount, Show } from 'solid-js';
import { Portal } from 'solid-js/web';
import { describeApiError } from '../lib/apiClient.js';
import './RelaySetupDialog.css';

const RELAY_API = '/api/relay/v1';

async function relayRequest(path, init) {
  const response = await fetch(RELAY_API + path, {
    credentials: 'include',
    ...init,
    headers: { 'content-type': 'application/json', ...(init?.headers || {}) },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(body.error || `HTTP ${response.status}`);
    error.status = response.status;
    throw error;
  }
  return body;
}

export default function RelaySetupDialog(props) {
  const [code, setCode] = createSignal(null);
  const [expiresAt, setExpiresAt] = createSignal(null);
  const [message, setMessage] = createSignal('');
  const [successNote, setSuccessNote] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  const [wizardActive, setWizardActive] = createSignal(false);
  const [esoInput, setEsoInput] = createSignal('');
  const [editingEso, setEditingEso] = createSignal(false);
  const [copied, setCopied] = createSignal(false);
  let closeButton;
  let trigger;
  let pollTimer = null;

  const [pairingStatus, { refetch: refetchStatus }] = createResource(
    () => props.open,
    () => relayRequest('/pairing-status').catch(() => null),
  );

  const [devices, { refetch: refetchDevices }] = createResource(
    () => props.open,
    () => relayRequest('/devices').then(result => result.devices),
  );

  const [download] = createResource(
    () => props.open,
    () => fetch(RELAY_API + '/download').then(async response => {
      if (!response.ok) return null;
      return response.json();
    }).catch(() => null),
  );

  createEffect(() => {
    const st = pairingStatus();
    if (st?.step2_eso?.atTag && !esoInput()) {
      setEsoInput(st.step2_eso.atTag);
    }
  });

  function stopPolling() {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  const GUILD_LABELS = { post: 'Trading Post', caravan: 'Caravan', dealers: 'Dealers' };
  const formatGuildList = (guilds) => (guilds || []).map(g => GUILD_LABELS[g.guild] || g.guild).join(', ');

  function startPolling() {
    stopPolling();
    pollTimer = setInterval(async () => {
      if (!props.open) return;
      if (code() && expiresAt() && Date.now() >= expiresAt()) {
        setCode(null);
        setExpiresAt(null);
        setMessage('Pairing code expired. Click "Create pairing code" to generate a fresh code.');
        return;
      }
      try {
        const prevCount = pairingStatus()?.step3_relay?.deviceCount || 0;
        const fresh = await refetchStatus();
        await refetchDevices();
        const nextCount = fresh?.step3_relay?.deviceCount || 0;
        if (nextCount > prevCount) {
          setSuccessNote('Windows Relay EXE paired and verified!');
          setCode(null);
          props.onChanged?.();
        }
      } catch { /* ignore background poll error */ }
    }, 3000);
  }

  createEffect(() => {
    if (props.open && (wizardActive() || code())) {
      startPolling();
    } else {
      stopPolling();
    }
  });

  function dismiss() {
    stopPolling();
    props.onClose?.();
    queueMicrotask(() => trigger?.focus());
  }

  function handleKey(event) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      dismiss();
    }
  }

  async function startGuidedPairing() {
    setWizardActive(true);
    setMessage('');
    setSuccessNote('');
    const st = await refetchStatus();
    if (!st?.step2_eso?.completed) {
      setEditingEso(true);
    }
    if (!code() && !(st?.step3_relay?.completed)) {
      await createCode();
    }
  }

  async function saveEsoTag(e) {
    e?.preventDefault?.();
    const raw = esoInput().trim();
    if (!raw) {
      setMessage('Enter your in-game ESO @UserID (for example @YourName).');
      return;
    }
    setBusy(true);
    setMessage('');
    setSuccessNote('');
    try {
      const res = await relayRequest('/link-profile', {
        method: 'POST',
        body: JSON.stringify({ atTag: raw }),
      });
      setEsoInput(res.atTag);
      setEditingEso(false);
      await refetchStatus();
      props.onChanged?.();
      setSuccessNote(
        res.inGuildRoster
          ? `Linked ${res.atTag} (verified in Redfur roster: ${formatGuildList(res.guilds)})`
          : `Linked ${res.atTag} to your Discord & Web profile.`
      );
    } catch (error) {
      setMessage(describeApiError(error, 'Link ESO @UserID'));
    } finally {
      setBusy(false);
    }
  }

  async function createCode() {
    setBusy(true);
    setMessage('');
    setSuccessNote('');
    try {
      const result = await relayRequest('/pairing-codes', { method: 'POST', body: '{}' });
      setCode(result.code);
      setExpiresAt(result.expiresAt);
      setWizardActive(true);
    } catch (error) {
      setMessage(describeApiError(error, 'Pairing code'));
    } finally {
      setBusy(false);
    }
  }

  async function copyCode() {
    if (!code()) return;
    try {
      await navigator.clipboard.writeText(code());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  }

  async function revoke(device) {
    setBusy(true);
    setMessage('');
    setSuccessNote('');
    try {
      await relayRequest(`/devices/${device.id}`, { method: 'DELETE', body: '{}' });
      await refetchDevices();
      await refetchStatus();
      props.onChanged?.();
    } catch (error) {
      setMessage(describeApiError(error, 'Disconnect relay'));
    } finally {
      setBusy(false);
    }
  }

  const completedStepsCount = () => {
    const st = pairingStatus();
    if (!st) return 0;
    let count = 0;
    if (st.step1_discord?.completed) count++;
    if (st.step2_eso?.completed) count++;
    if (st.step3_relay?.completed || (devices()?.length || 0) > 0) count++;
    return count;
  };

  onMount(() => {
    trigger = document.activeElement;
    document.addEventListener('keydown', handleKey, true);
    queueMicrotask(() => closeButton?.focus());
  });
  onCleanup(() => {
    stopPolling();
    document.removeEventListener('keydown', handleKey, true);
  });

  return (
    <Show when={props.open}>
      <Portal>
        <div class="v2-relay__backdrop" role="dialog" aria-modal="true" aria-labelledby="v2-relay-title" onClick={(event) => event.target === event.currentTarget && dismiss()}>
          <section class="v2-relay">
            <header class="v2-relay__header">
              <div>
                <p class="v2-relay__eyebrow">FISSAL RELAY · SETUP &amp; PAIRING</p>
                <h2 id="v2-relay-title">Connect your desktop relay</h2>
              </div>
              <button ref={closeButton} type="button" class="v2-relay__close" onClick={dismiss} aria-label="Close Fissal Relay setup">✕</button>
            </header>

            <div class="v2-relay__anon-banner">
              <div class="v2-relay__anon-badge">ANONYMOUS SYNC READY</div>
              <p>
                <strong>Works out-of-the-box:</strong> Anyone can download and run <code>RedfurSync.exe</code> immediately without pairing — it will sync non-Redfur guild bank data anonymously (quarantine-isolated for safety).
                <strong> All pairing steps below are optional</strong>, but suggested for full Redfur recognition (Sync Points, Auto-Ranks, Raffle &amp; Trader attribution).
              </p>
            </div>

            <div class="v2-relay__actions">
              <Show when={download()?.clientUrl || download()?.url} fallback={<a class="v2-relay__download" href="https://github.com/EchoTiger/ESOSyncClient/releases" target="_blank" rel="noreferrer">Open Relay releases</a>}>
                <a class="v2-relay__download" href={download()?.clientUrl || download()?.url} target="_blank" rel="noopener noreferrer">
                  ⚡ Download Windows Relay (.exe) {download()?.clientVersion ? `(v${download().clientVersion})` : ''}
                </a>
              </Show>
              <Show when={download()?.addonUrl}>
                <a class="v2-relay__download v2-relay__download--addon" href={download().addonUrl} title="Optional: Windows Relay automatically installs and updates the AddOn">
                  📜 Download ESO Addon (Optional)
                </a>
              </Show>
              <button type="button" class="v2-relay__start-btn" disabled={busy()} onClick={startGuidedPairing}>
                🚀 Start Pairing (1 · 2 · 3 Checklist)
              </button>
            </div>

            <div class="v2-relay__checklist" classList={{ 'v2-relay__checklist--active': wizardActive() }}>
              <div class="v2-relay__checklist-header">
                <span>GUIDED PAIRING CHECKLIST (OPTIONAL · SUGGESTED)</span>
                <span class="v2-relay__checklist-progress">{completedStepsCount()} / 3 Linked</span>
              </div>

              {/* STEP 1: Discord & Web Client */}
              <div class="v2-relay__step-card" classList={{ 'v2-relay__step-card--done': Boolean(pairingStatus()?.step1_discord?.completed) }}>
                <div class="v2-relay__step-top">
                  <span class="v2-relay__step-num">1</span>
                  <div class="v2-relay__step-title">
                    <strong>Discord &amp; Web Client</strong>
                    <small>Links your Discord display name so syncs never show raw IDs</small>
                  </div>
                  <Show
                    when={pairingStatus()?.step1_discord?.completed}
                    fallback={<span class="v2-relay__step-badge v2-relay__step-badge--opt">Sign In Required</span>}
                  >
                    <span class="v2-relay__step-badge v2-relay__step-badge--ok">✓ Linked</span>
                  </Show>
                </div>
                <div class="v2-relay__step-body">
                  <Show
                    when={pairingStatus()?.step1_discord?.completed}
                    fallback={<a class="v2-relay__pair" href="/auth/discord">Sign in with Discord</a>}
                  >
                    <span class="v2-relay__pill">
                      Discord: <strong>{pairingStatus()?.step1_discord?.displayName || pairingStatus()?.step1_discord?.username || 'Connected'}</strong>
                    </span>
                  </Show>
                </div>
              </div>

              {/* STEP 2: In-Game ESO @UserID */}
              <div class="v2-relay__step-card" classList={{ 'v2-relay__step-card--done': Boolean(pairingStatus()?.step2_eso?.completed) }}>
                <div class="v2-relay__step-top">
                  <span class="v2-relay__step-num">2</span>
                  <div class="v2-relay__step-title">
                    <strong>In-Game ESO @UserID</strong>
                    <small>Credits your ESO account for Sync Points, Raffle &amp; Auto-Ranks</small>
                  </div>
                  <Show
                    when={pairingStatus()?.step2_eso?.completed}
                    fallback={<span class="v2-relay__step-badge v2-relay__step-badge--opt">Optional</span>}
                  >
                    <span class="v2-relay__step-badge v2-relay__step-badge--ok">✓ Linked</span>
                  </Show>
                </div>
                <div class="v2-relay__step-body">
                  <Show
                    when={!pairingStatus()?.step2_eso?.completed || editingEso()}
                    fallback={
                      <div class="v2-relay__eso-summary">
                        <span class="v2-relay__pill">
                          ESO Tag: <strong>{pairingStatus()?.step2_eso?.atTag}</strong>
                          <Show when={pairingStatus()?.step2_eso?.inGuildRoster}>
                            {' '}· <em class="v2-relay__roster-ok">Verified in Roster ({formatGuildList(pairingStatus().step2_eso.guilds)})</em>
                          </Show>
                        </span>
                        <button type="button" class="v2-relay__inline-btn" onClick={() => setEditingEso(true)}>Edit @UserID</button>
                      </div>
                    }
                  >
                    <form class="v2-relay__eso-form" onSubmit={saveEsoTag}>
                      <input
                        type="text"
                        class="v2-relay__input"
                        placeholder="@YourESOTag (e.g. @chuchui)"
                        value={esoInput()}
                        onInput={(e) => setEsoInput(e.currentTarget.value)}
                        maxLength={33}
                      />
                      <button type="submit" class="v2-relay__pair" disabled={busy()}>Save @UserID</button>
                      <Show when={pairingStatus()?.step2_eso?.completed}>
                        <button type="button" class="v2-relay__inline-btn" onClick={() => setEditingEso(false)}>Cancel</button>
                      </Show>
                    </form>
                    <small class="v2-relay__hint">Tip: Even if skipped here, your first verified <code>FissalRelay.lua</code> sync will auto-detect your @UserID.</small>
                  </Show>
                </div>
              </div>

              {/* STEP 3: Windows Relay EXE */}
              <div class="v2-relay__step-card" classList={{ 'v2-relay__step-card--done': Boolean((devices()?.length || 0) > 0) }}>
                <div class="v2-relay__step-top">
                  <span class="v2-relay__step-num">3</span>
                  <div class="v2-relay__step-title">
                    <strong>Windows Relay EXE</strong>
                    <small>Enter a 6-digit code in RedfurSync.exe to upgrade from Anonymous to Verified</small>
                  </div>
                  <Show
                    when={(devices()?.length || 0) > 0}
                    fallback={<span class="v2-relay__step-badge v2-relay__step-badge--opt">Optional</span>}
                  >
                    <span class="v2-relay__step-badge v2-relay__step-badge--ok">✓ Paired ({devices().length})</span>
                  </Show>
                </div>

                <div class="v2-relay__step-body">
                  <Show when={devices.loading}><p class="v2-relay__status">Checking paired devices...</p></Show>
                  <Show when={!devices.loading && !devices()?.length}>
                    <p class="v2-relay__status">No desktop relay is paired to this account yet (running in Anonymous Mode until paired).</p>
                  </Show>
                  <For each={devices() || []}>{(device) => (
                    <div class="v2-relay__device">
                      <div>
                        <strong>{device.name} {device.clientVersion ? `(v${device.clientVersion})` : ''}</strong>
                        <span>{device.lastSeenAt ? `Last seen ${new Date(device.lastSeenAt).toLocaleString()}` : 'Awaiting first signal'}</span>
                      </div>
                      <button type="button" class="v2-relay__text-action" disabled={busy()} onClick={() => revoke(device)}>Disconnect</button>
                    </div>
                  )}</For>

                  <div class="v2-relay__step3-actions">
                    <button type="button" class="v2-relay__pair" disabled={busy()} onClick={createCode}>
                      {code() ? 'Generate New 6-Digit Code' : 'Create pairing code'}
                    </button>
                  </div>

                  <Show when={code()}>
                    <div class="v2-relay__code" aria-live="polite">
                      <span>Paste this 6-digit code into RedfurSync.exe → Pair</span>
                      <div class="v2-relay__code-row">
                        <strong>{code()}</strong>
                        <button type="button" class="v2-relay__copy-btn" onClick={copyCode}>
                          {copied() ? '✓ Copied' : 'Copy Code'}
                        </button>
                      </div>
                      <small>
                        Expires {expiresAt() ? new Date(expiresAt()).toLocaleTimeString() : 'soon'} · ⟳ Listening for your desktop relay...
                      </small>
                    </div>
                  </Show>
                </div>
              </div>
            </div>

            <p class="v2-relay__release-note">
              Direct Windows executable hosted securely on GitHub Releases &amp; Redfur Relay. Note: TamrielTradeCentre is harvested autonomously by our server — your Relay only watches <code>SavedVariables</code>.
            </p>
            <Show when={successNote()}><p class="v2-relay__success" role="status">✓ {successNote()}</p></Show>
            <Show when={message()}><p class="v2-relay__message" role="status">{message()}</p></Show>
          </section>
        </div>
      </Portal>
    </Show>
  );
}
